import { makeId } from "@/common/id";
import { Company } from "../model/company";
import { Services } from "../services";
import { I18NRepository, Translates } from "./i18n";
import { LoginData, User, UserSession, UsersRepository } from "./users";

const loginUrl = '/login'

const loginPaths: (string | RegExp)[] = [
    '/',
    loginUrl
]

const allowedPaths: (string | RegExp)[] = [
    ...loginPaths,
    '/registration',
    /^\/registration\/.*/,
    '/resetpassword',
    '/qr/tag',
    /^\/qr\/tag\/.*/,
    '/communal',
    /^\/communal\/.*/,
]

// Function to check if the URL is allowed for anon users
export function isAllowedUrl(url: string): boolean {
    return allowedPaths.some(path => {
        if (typeof path === 'string') {
            return url === path;
        } else if (path instanceof RegExp) {
            return path.test(url);
        }
        return false;
    });
}

// Function to check if the URL is allowed for anon users
export function isLoginUrl(url: string): boolean {
    return loginPaths.some(path => {
        if (typeof path === 'string') {
            return url === path;
        } else if (path instanceof RegExp) {
            return path.test(url);
        }
        return false;
    });
}

const currentSessionStorageKey = "session"

const logoutMinutesTimeBuffer = 15 //15min before token expired

export interface CurrentSession extends UserSession {
    translates: Translates
}

export type Listener = (data: CurrentSession | null | undefined) => void

export class SessionService {
    #current: CurrentSession | undefined | null;
    #inited: boolean = false;
    #i18n: I18NRepository
    #users: UsersRepository
    #listeners = new Map<string, Listener>()

    constructor({ users, i18n }: { users: UsersRepository, i18n: I18NRepository }) {
        this.#users = users
        this.#i18n = i18n
        this.#current = undefined
        this.initSession()
        setTimeout(async () => {
            this.#set(await this.loadSession())
            window.addEventListener('popstate', this.#redirectToLoginIfNeeded)
            window.addEventListener('hashchange', this.#redirectToLoginIfNeeded)
            setInterval(this.#redirectToLoginIfNeeded, 2000)
            this.#redirectToLoginIfNeeded()
        }, 0)
    }

    async initSession() {
        if (this.#current !== undefined) {
            return
        }
        this.#set(await this.loadSession())
    }

    #echo(data: CurrentSession | null | undefined) {
        this.#listeners.forEach((listener: Listener) => {
            listener(data)
        });
    }

    #set(data: CurrentSession | null | undefined) {
        this.#current = data
        this.#inited = true
        this.#echo(data)
    }

    #redirectToLoginIfNeeded = () => {
        if (this.#current) {
            const logoutAt = new Date(this.#current.expiresAt)
            logoutAt.setMinutes(logoutAt.getMinutes() - logoutMinutesTimeBuffer)
            if (logoutAt < new Date()) {
                this.#removeSession()
                window.location.href = loginUrl
            }
            return
        }
        if (!isAllowedUrl(location.pathname)) {
            window.location.href = loginUrl
        }
    }

    listen(listener: Listener): string {
        var id = makeId(12)
        this.#listeners.set(id, listener)
        if (this.#inited) {
            listener(this.#current)
        }
        return id
    }

    remove(id: string) {
        this.#listeners.delete(id)
    }

    async fetchWithToken(url: string, options: RequestInit = {}): Promise<Response> {
        const headers = new Headers(options.headers);
        if (this.#current?.token) {
            headers.set('Authorization', `Bearer ${this.#current.token}`);
        }
        if (this.#current?.user.lang) {
            headers.set('Accept-Language', this.#current.user.lang);
        }
        return fetch(url, {
            ...options,
            headers,
        }).then(resp => {
            if (resp.status === 401) {
                this.logout(true)
            }
            return resp
        });
    }

    isLoggedIn(): boolean {
        return !!this.#current
    }

    async login(data: LoginData): Promise<CurrentSession> {
        const userSession = await this.#users.login(data)
        return this.loadUserSession(userSession)
    }

    async loginByCard(ucid: string): Promise<CurrentSession> {
        const userSession = await this.#users.loginByCard(ucid)
        return this.loadUserSession(userSession)
    }

    async loadUserSession(userSession: UserSession) {
        const translates = await this.#i18n.get('main', userSession.user.lang)
        const currentSession = {
            ...userSession,
            translates
        }
        this.#saveSession(currentSession)
        this.#set(currentSession)
        return currentSession
    }

    #saveSession(session: CurrentSession) {
        localStorage.setItem(currentSessionStorageKey, JSON.stringify({
            token: session.token,
            user: session.user,
            expiresAt: session.expiresAt
        }))
    }

    #removeSession() {
        localStorage.removeItem(currentSessionStorageKey)
    }

    async loadSession(): Promise<CurrentSession | null | undefined> {
        if (this.#current !== undefined) {
            return this.#current
        }
        const userSessionItem = localStorage.getItem(currentSessionStorageKey)
        if (userSessionItem === null) {
            return null
        }
        const userSession = JSON.parse(userSessionItem)
        if (!userSession) {
            return null
        }
        const translates = await this.#i18n.get('main', userSession.user.lang)
        return {
            ...userSession,
            translates
        }
    }

    async logout(redirect: boolean = true): Promise<void> {
        this.#set(null)
        localStorage.removeItem(currentSessionStorageKey)
        if (redirect) {
            this.#redirectToLoginIfNeeded()
        }
        return
    }

    async setLang(lang: string): Promise<void> {
        if (this.#current === undefined) {
            throw new Error("User is not login")
        }
        if (this.#current === null) {
            throw new Error("User is loading")
        }
        const session = {
            ...this.#current,
            user: {
                ...this.#current.user,
                lang: lang
            }
        }
        const updateLang$ = this.fetchWithToken(`/api/schema/users/byid/${session.user.id}/update-lang`, {
            method: 'POST',
            body: JSON.stringify({
                id: session.user.id,
                lang: lang
            })
        })
        const translates$ = this.#i18n.get('main', lang)
        const [, translates] = await Promise.all([updateLang$, translates$])

        session.translates = translates
        this.#saveSession(session)
        this.#set(session)
    }
}